import type { Role } from "./types";

/**
 * Roles: the rules the panel applies before it asks the server. Like
 * `lib/operator.ts`, none of this is authority — the API checks every one again.
 */

/** The server's limit: 1–60 characters, unique case-insensitively. */
export const ROLE_NAME_MAX = 60;

export interface RoleFormIssues {
  readonly name?: string;
}

/** An empty name reports nothing yet, for `inviteFormIssues`'s reason. */
export function roleFormIssues(form: { readonly name: string }): RoleFormIssues {
  const name = form.name.trim();
  if (form.name !== "" && name === "") return { name: "A role needs a name." };
  if (name.length > ROLE_NAME_MAX) return { name: `At most ${ROLE_NAME_MAX} characters.` };
  return {};
}

/** Administrator first, then by name — the server's order, kept if a cache reorders. */
export function sortRoles(roles: readonly Role[]): readonly Role[] {
  return [...roles].sort(
    (a, b) =>
      Number(b.grantsAll) - Number(a.grantsAll) ||
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
  );
}

/** What the delete confirmation says, so the consequence is read before it is done. */
export function deleteConsequence(role: Role): string {
  if (role.memberCount === 0) return `No operator holds ${role.name}.`;
  const who = role.memberCount === 1 ? "1 operator loses" : `${role.memberCount} operators lose`;
  return `${who} everything ${role.name} grants, unless another role or a direct permission covers it.`;
}

/** Whether a name is already taken by another role, ignoring case like the server. */
export function nameTaken(roles: readonly Role[], name: string, exceptId?: string): boolean {
  const wanted = name.trim().toLowerCase();
  return roles.some((role) => role.id !== exceptId && role.name.toLowerCase() === wanted);
}
